// ---------- installation and offline (web only) ----------
// Registers the service worker emitted by the build (cf. swPlugin in vite.config.ts). That
// worker is what makes the web version installable and playable with no network; the
// manifest and the icons live in public/.
//
// It also watches for a new build being deployed while the app is open, and offers it: the
// worker itself never steps in front of the one running (cf. the comment on `install` in
// sw.js), so without a prompt a player who never closes the tab would stay on the build they
// first loaded, indefinitely.

import { t } from './i18n';
import { isNative } from './platform';

const $ = <T extends HTMLElement = HTMLElement>(id: string): T => document.getElementById(id) as T;

/** How often a page left open asks the server whether a new build exists. Long on purpose:
 *  every tick is a request, and the session that lasts hours is precisely the one that must
 *  not spend a phone's battery on polling. Coming back to the app is what catches most
 *  updates; this only covers the tab nobody ever leaves. */
const CHECK_MS = 30 * 60_000;
/** Two checks closer together than this are one check. Returning to the app fires as often as
 *  the player switches windows, and each one would otherwise hit the network. */
const MIN_GAP_MS = 5 * 60_000;

let lastCheck = Date.now(); // registering already fetched the worker: that WAS a check
let offered = false;
let reloading = false;

/** Asks the browser to re-fetch sw.js. A byte-identical answer is a no-op — no `updatefound`,
 *  no dialog — so this is cheap to call whenever there is a reason to. */
function check(reg: ServiceWorkerRegistration): void {
  const now = Date.now();
  if (now - lastCheck < MIN_GAP_MS) return;
  lastCheck = now;
  void reg.update().catch(() => {
    /* offline, or the server is down: the next check does the job */
  });
}

/**
 * The new build is downloaded and waiting. The dialog has one button and no way out — no
 * close, no dismissal on the backdrop: an app split across two builds is not a state worth
 * supporting, so the player reloads onto the new one.
 *
 * It costs them nothing to lose: the taps in flight are flushed on pagehide (cf.
 * wireProgressFlush), so the sheet comes back exactly as it was left.
 */
function offer(waiting: ServiceWorker): void {
  if (offered) return;
  offered = true;
  const btn = $<HTMLButtonElement>('updNow');
  $('update').setAttribute('aria-hidden', 'false');
  btn.focus();
  btn.onclick = () => {
    btn.disabled = true;
    btn.textContent = t('update.reloading');
    // Reload as soon as the new worker has taken over, and not before: reloading first would
    // only re-open the build being replaced. The listener is attached HERE and not at startup
    // because the very first install claims the page too — a reload on that would restart the
    // app under a player who had merely opened it.
    const done = (): void => {
      if (reloading) return;
      reloading = true;
      location.reload();
    };
    navigator.serviceWorker.addEventListener('controllerchange', done);
    waiting.postMessage({ type: 'SKIP_WAITING' });
    // If that worker was already gone — activated by another tab, or discarded — no
    // controllerchange is coming and the button would sit there dead. Reload regardless.
    window.setTimeout(done, 2500);
  };
}

/** A worker is only an UPDATE if something was already controlling the page: on a first visit
 *  the same event means "the app is now installed", which is no news to anyone. */
function watch(reg: ServiceWorkerRegistration): void {
  if (reg.waiting && navigator.serviceWorker.controller) offer(reg.waiting);
  reg.addEventListener('updatefound', () => {
    const sw = reg.installing;
    if (!sw) return;
    sw.addEventListener('statechange', () => {
      if (sw.state === 'installed' && navigator.serviceWorker.controller) offer(sw);
    });
  });
}

/**
 * Fire and forget: everything here is a bonus, and nothing in the game waits on it.
 *
 * Two surfaces are left out on purpose:
 *  - the APK, whose assets are already local — Capacitor serves them from its own scheme,
 *    and a worker there would only add a cache in front of a filesystem;
 *  - dev, where sw.js doesn't exist (the plugin is build-only) and a cache would sit in
 *    front of the HMR.
 *
 * L'admin figurait dans cette liste tant qu'elle embarquait le jeu en iframe pour convertir une
 * photo. Elle ne l'embarque plus : elle convertit elle-même (cf. DEPLOY.md), et le jeu n'est
 * plus jamais chargé ailleurs que chez un joueur.
 */
export function initPWA(): void {
  if (isNative() || !import.meta.env.PROD) return;
  if (!('serviceWorker' in navigator)) return;

  // After load: registration competes with the first paint otherwise, and on a phone that
  // shows.
  window.addEventListener('load', () => {
    void navigator.serviceWorker
      .register(new URL('sw.js', document.baseURI), {
        // Without this the browser may check for a new worker through the HTTP cache, and a
        // deploy would take hours to reach a returning player.
        updateViaCache: 'none',
      })
      .then((reg) => {
        watch(reg);
        // The check that matters on a phone: an app is left in the background for days and
        // comes back, and this is the moment it can learn it is out of date.
        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState === 'visible') check(reg);
        });
        window.setInterval(() => check(reg), CHECK_MS);
      })
      .catch(() => {
        /* refused (private window, unsupported), and the game plays on regardless */
      });
  });
}
